
import { useEffect, FC, useContext } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { classes, media, style } from 'typestyle'

/* Common ======================================================================================= */
import { withRouter } from '../../../contexts/with-router'
import { get } from '../../../share/find'

/* Hooks ======================================================================================== */
import useValue from '../../../hooks/use-value'

/* Modules ====================================================================================== */
import '../../snippets'
import '../../../../fields'

/* Contexts ===================================================================================== */
import { NavigationContext } from '../../../contexts/navigation'

/* Components =================================================================================== */
import AppLayout from '../app/layout'
import { BodyContent } from '../../../modules/layouts/body/body-content'
import { BodyHead } from '../../../modules/layouts/body/body-head'
import { Box } from '../../../components/box/box.component'
import { Modal } from '../../../components/modal'
import { renderSnippets } from '../../../modules/handlers/render-snippets'

/* Styles ======================================================================================= */
import { pb36 } from '../../../styles/styleset/padding/pb36'
import { f16i } from '../../../styles/styleset/font-size/f16i'

const ComponentWithOffsetTop = (props: any) => {
  const appLayoutContext = useContext(AppLayout.context)
  const { getOffsetTop = () => 0 } = appLayoutContext || ({} as any)
  return (
    <>
      {renderSnippets({
        ...props,
        offsetTop: getOffsetTop(),
      })}
    </>
  )
}

/* <View /> ===================================================================================== */
const View: FC<any> = ({ eject }) => {
  const params = useParams()
  const [searchParams] = useSearchParams()
  const { groupId } = params
  const { navigation, snippets, snippetPreset, $snippetPreset, isSectionalSnippets } = useContext(
    NavigationContext,
  )

  const back = get('payload.back', navigation)
  const base = get('payload.base', navigation, 'object')
  const actions = get('payload.base.row.actions', navigation, 'array')
  const name = get('name', base, 'object')
  const viewStyle = get('payload.base.row.style', navigation, 'object')

  const [row, $row] = useValue({})
  const [snippetTriggers, $snippetTriggers] = useValue({})

  useEffect(() => {
    return () => {
      eject()
    }
  }, [])

  function getActions() {
    if (actions) {
      return [
        ...actions
          .filter((a: any) => a.target === 'row')
          .map((action: any) => {
            let onClick

            if (action.type === 'request' || action.type === 'delete') {
              onClick = () => {
                if (action.type === 'request' || action.type === 'delete') {
                  for (const key in params) {
                    action.to.url = action.to.url.replace(`{{${key}}}`, params[key])
                  }

                  Modal.push({
                    type: 'Request',
                    componentProps: {
                      to: action.to,
                      label: action.description,
                      destination: action.type === 'delete' ? back : null,
                    },
                  })
                }
              }
            }
            return {
              ...action,
              onClick,
              to: (location: any) => {
                if (action.to[0] === '/') {
                  return `${action.to}`.replace('{{groupId}}', groupId || '')
                } else {
                  return `${location.pathname}/${`${action.to}`.replace(
                    '{{groupId}}',
                    groupId || '',
                  )}`
                }
              },
              icon: action.icon && (
                <i className={classes('material-icons-two-tone', f16i)}>{action.icon}</i>
              ),
            }
          }),
      ]
    }

    return []
  }

  return (
    <AppLayout.render
      head={{
        title: `${process.env.REACT_APP_PROJECT_NAME} | ${getSubtitle({
          base: base,
          data: row,
          name,
        })} | ${getTitle({
          base: base,
          data: row,
          name,
        })}`,
      }}
      headerProps={{
        maxWidth: '100%',
      }}
      toolbarProps={{
        title: `${getSubtitle({ base: base, data: row, name })} | ${getTitle({
          base: base,
          data: row,
          name,
        })}`,
        row,
      }}
      content={
        <Box
          className={classes(
            pb36,
            style({
              ...media(
                { type: 'print' },
                {
                  background: 'white !important',
                },
              ),
            }),
          )}
          baseline="background"
          fullWidth
          fullHeight
        >
          {/* Header */}
          <BodyHead
            maxWidth={viewStyle?.width}
            subtitle={getSubtitle({ base: base, data: row, name }) || '-'}
            title={getTitle({ base: base, data: row, name }) || '-'}
            actions={getActions()}
            viewMode={
              isSectionalSnippets && Object.keys(snippets || {}).length > 0
                ? {
                    value: snippetPreset,
                    options: Object.keys(snippets),
                    onChange: (value: string) => {
                      $snippetPreset.set(value)
                    },
                  }
                : undefined
            }
          />
          {/* Content */}
          <BodyContent maxWidth={viewStyle?.width}>
            {(() => {
              if (snippets) {
                const temp = {
                  snippets: snippets?.[snippetPreset]?.items || snippets,
                  update: (id: any) => {
                    $snippetTriggers.set((prev: any) => ({ ...prev, [id]: Date.now() }))
                  },
                  sync: snippetTriggers,
                  $snippetPreset,
                  $row,
                  row,
                }
                return <ComponentWithOffsetTop {...temp} />
              }
            })()}
          </BodyContent>
        </Box>
      }
    />
  )
}

export const getSubtitle = (params: { base: any; data: any; name: string }) => {
  const { name } = params
  const singular = get('singular', name, 'string', '-')

  return singular
}

export const getTitle = (params: { base: any; data: any; name: string }) => {
  const { base, data } = params
  const primaryKey = get((base.row && base.row.primaryKey) || base.primaryKey, data, 'string', '-')

  return primaryKey
}

export default withRouter(View)
